'use client'

import { GlobalDataContext } from "@/app/context/global/globalDataContext";
import { getCreatureDetailsHelper } from "@/app/helpers/getCreatureDetailsHelper";
import { Text, Badge, Box, Link, Flex, Image, Progress, VStack, Skeleton, useDisclosure } from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useState } from "react";
import NextLink from "next/link";
import { GlobalDataUserContext } from "@/app/context/global/globalDataUserContext";
import { CreatureDefinitionType } from "@/app/types/creatureDefinitionType";
import { GiCupcake } from "react-icons/gi";
import { FaArrowsRotate, FaStar } from "react-icons/fa6";
import { buildGameUrl } from "@/app/helpers/buildUrlHelper";
import {
    PiSmileyFill,
    PiSmileyWinkFill,
    PiSmileyMehFill,
    PiSmileySadFill,
    PiSmileyNervousFill
} from 'react-icons/pi';
import { SwitchCreature } from "./modal/switchCreature";
import { FaHourglassStart } from 'react-icons/fa';

type Props = {
    id: string;
    creatureId : string;
    eating: number;
    laughing: number
    available: boolean;
    double?: boolean
    shadow?: boolean
    isNew?: boolean
    shouldPuff?: boolean
};

 export default function CreatureCard({id, creatureId, eating, laughing, available, double=false, shadow= false, isNew=false, shouldPuff=false}: Props) {

    //const [creature, setCreature] = useState<CreatureDefinitionType>(getCreatureDetailsHelper(creatureId)!)
    
    // Utilisation de useMemo pour mémoriser les détails de la créature
    const creatureDetails = useMemo(() => getCreatureDetailsHelper(creatureId)!, [creatureId]);

    // Si vous avez toujours besoin de setCreature pour des mises à jour manuelles
    const [creature, setCreature] = useState<CreatureDefinitionType>(creatureDetails);

    const {
        language,
    } = useContext(GlobalDataContext)
    
    const {
        feedCreature,
        laughCreature,
        isMyCreaturesLoading,
        doPuff,
        tradeRequests,
        addNewCreature,
        addFood,
        openSwitch
    } = useContext(GlobalDataUserContext)

    const [isLaughing, setIsLaughing] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isCardInTrade, setIsCardInTrade] = useState(false);

    useEffect(()=>{
        if ( shouldPuff) {
            doPuff()
        }
    },[shouldPuff])

    const makeCreatureLaugh=(id:string)=>{        
        setIsLaughing(true)        
        laughCreature(id)
    }

    useEffect(()=>{
        if ( isLaughing) {
            const tiemout = setTimeout(()=>{
                setIsLaughing(false)
            }, 1000)

            return () => clearTimeout(tiemout)
        }
    },[isLaughing])

    const checkIsCardInTrade = useMemo(() => {
        return (idTo:string) => {
          return tradeRequests && tradeRequests.length > 0
            ? !!tradeRequests.find((tradeRequest) =>
                (tradeRequest.senderOriginalCreatureId === idTo || 
                 tradeRequest.receiverOriginalCreatureId === idTo) && 
                tradeRequest.status === "pending"
              )
            : false;
        };
      }, [tradeRequests]);
/*
    const checkIsCardInTrade=(idTo:string)=> tradeRequests && tradeRequests.length > 0 
    ? !!tradeRequests.find((tradeRequest) => 
        (tradeRequest.senderOriginalCreatureId === idTo || 
         tradeRequest.receiverOriginalCreatureId === idTo) && 
         tradeRequest.status === "pending"
      ) 
    : false;
*/

    useEffect(()=>{
        setIsCardInTrade(checkIsCardInTrade(id))
    },[id, tradeRequests])

    return (
        <>
        <Skeleton isLoaded={!isMyCreaturesLoading} borderRadius="20px">
            <Box 
                className={(isNew||isLaughing||shouldPuff)?"shake":""}
                textAlign="center"
                position="relative"
                opacity={available?1:0.2}
                filter={available?'':'grayscale(1)'}
                border={(creature.type=="LEGENDARY"||creature.type=="AQUATIC"||creature.type=="FLYING")?"5px solid transparent":"3px solid white"}
                background={creature.type=="LEGENDARY"?"#eba628":(creature.type=="FLYING"?"red":"#3797d1")}            
                borderRadius="20px"
                overflow={"hidden"}
                //boxShadow={" 0 1px 3px 0 rgb(0 0 0), 0 1px 2px 0 rgb(0 0 0 / 61%);"}
                >
                    {id !=creatureId && !shadow && <Link 
                        as={NextLink} 
                        href={buildGameUrl(language, creature.category, creatureId)}
                        shallow={true}                    
                        >
                        <Image 
                            opacity={0.75+0.25*eating/10}
                            filter={"grayscale("+0.75+0.25*(eating/10)+")"}
                            src={creature.url}
                            maxW={"100%"}  
                            alt="monster"
                            loading="lazy"
                            background="transparent"
                            borderRadius="20px"
                            overflow={"hidden"}
                            minH={"206px"}
                            />
                            {isNew && 
                                <Box position="absolute" zIndex="2" bg='#00000091' color="orange" borderRadius="50%" bottom={"5px"} right={"5px"} p={1}>
                                    <FaStar fontSize={"1em"}/>
                                </Box>
                            }
                    </Link>}
                    {id ==creatureId && 
                        <Image 
                            opacity={0.75+0.25*eating/10}
                            src={creature.url}
                            maxW={"100%"}  
                            alt="monster"
                            loading="lazy"
                            minH={"206px"}
                            />
                    }
                    {shadow && <Image 
                            opacity={0.75+0.25*eating/10}
                            filter={"grayscale("+0.75+0.25*(eating/10)+")"}
                            src={creature.url}
                            maxW={"100%"}  
                            alt="monster"
                            loading="lazy"
                            background="transparent"
                            borderRadius="20px"
                            overflow={"hidden"}
                            minH={"206px"}
                            />}
                <Flex 
                    position="absolute"
                    bottom={3}
                    w="full"
                    justifyContent={"center"}
                    flexDir={"column"}
                    placeItems={"center"}
                >
                    <Text 
                        background={creature.type=="LEGENDARY"? "#eba628":(creature.type=="AQUATIC"? "#3797d1":(creature.type=="FLYING"?"red":"#00000099"))} 
                        px={3} 
                        pr={1}
                        py={1} 
                        fontSize={"0.9em"} 
                        borderRadius={10} 
                        fontWeight="bold" 
                        color={creature.type=="LEGENDARY"?"white":"white"}
                        lineHeight={"normal"}
                    >
                        {creature.name.toUpperCase()}
                        {creature.level && <Badge minW="21px" ml={2} p={1} borderRadius={"10"}>{creature.level}</Badge>}
                    </Text>              
                </Flex>
                {!shadow && available && <VStack w="full" position="absolute"
                        top={0}>
                    <Flex
                        w="full"
                        px={1}
                        py={1}
                        alignItems={"center"}
                        background='linear-gradient(to bottom, #000000c4, transparent)!important'
                    >
                        <Badge 
                            w="36px"
                            h="36px"
                            opacity={eating==10?0.75:1}
                            filter={eating==10?'grayscale(1)':''}
                            background={(eating>7?'#5f9e6e':(eating>2?'#d69e2e':'#e53e3e'))}
                            className={( eating == 10) ? '': (eating>7?'pulse-green':(eating>2?'pulse-yellow':'pulse-red'))} borderRadius="50%" fontSize={"1.5em"}
                            border={eating==10?'0px solid #4544449e':'0'}
                            onClick={() => { if( eating<10) feedCreature(id, 10-eating) }}
                            alignContent={"center"}
                            color={eating==10?"#000000c7":"white"}
                            position="relative"
                        >
                            <GiCupcake />
                            {eating < 10 &&<Badge position="absolute" bottom="0" right="0">{10-eating}</Badge>}
                        </Badge>
                        <Box
                            w="full"
                            border="3px solid #02020242"
                            borderLeft="0"
                        >              
                            <Progress className="eating-progress" transition={"0.5s"} background="black" colorScheme={(eating>7)?'green':(eating>2?'yellow':'red')} size='xs' value={eating*10} />
                        </Box>
                        <Badge
                            w="36px"
                            h="36px"
                            opacity={laughing==5?0.75:1}
                            filter={laughing==5?'grayscale(1)':''}
                            background={"transparent"}
                            className={( laughing == 5) ? '': (laughing>3?'pulse-green':(laughing>1?'pulse-yellow':'pulse-red'))} borderRadius="50%" fontSize={"1.5em"}
                            border={laughing==5?'0px solid #4544449e':'0'}
                            onClick={() => { if( laughing<5) makeCreatureLaugh(id) }}
                            alignContent={"center"}
                            color={(laughing>3?'#5f9e6e':(laughing>1?'#d69e2e':'#e53e3e'))}
                            paddingInline={0}
                            position="relative"
                        >
                            {laughing >=5 && <PiSmileyWinkFill style={{background:"#0000008c", borderColor:(laughing>3?'#5f9e6e':(laughing>1?'#d69e2e':'#e53e3e')), borderWidth: "3px",  overflow:"hidden", borderRadius:"100%"}} fontSize={"1.4em"}/>}
                            {laughing ==4 && <PiSmileyFill style={{background:"#0000008c", borderColor:(laughing>3?'#5f9e6e':(laughing>1?'#d69e2e':'#e53e3e')), borderWidth: "3px",  overflow:"hidden", borderRadius:"100%"}} fontSize={"1.4em"}/>}
                            {laughing ==3 && <PiSmileyMehFill style={{background:"#0000008c", borderColor:(laughing>3?'#5f9e6e':(laughing>1?'#d69e2e':'#e53e3e')), borderWidth: "3px",  overflow:"hidden", borderRadius:"100%"}} fontSize={"1.4em"}/>}
                            {laughing ==2 && <PiSmileySadFill style={{background:"#0000008c", borderColor:(laughing>3?'#5f9e6e':(laughing>1?'#d69e2e':'#e53e3e')), borderWidth: "3px",  overflow:"hidden", borderRadius:"100%"}} fontSize={"1.4em"}/>}
                            {laughing <=1 && <PiSmileyNervousFill style={{background:"#0000008c", borderColor:(laughing>3?'#5f9e6e':(laughing>1?'#d69e2e':'#e53e3e')), borderWidth: "3px",  overflow:"hidden", borderRadius:"100%"}} fontSize={"1.4em"}/>}

                        </Badge>
                    </Flex>
                    {!shadow && double && <Flex                    
                        px={1}
                        py={0}  
                        alignSelf={"self-start"}
                    >
                        {!isCardInTrade &&<Badge
                            p={1}
                            background="white"
                            borderRadius={"50%"}
                            w="32px"
                            h="32px"
                            display="flex"
                            placeContent="center"
                            alignItems="center"
                            className="pulse-white"
                            onClick={()=>openSwitch({...creature,creatureId:id})}
                        >
                            <FaArrowsRotate color="black" fontSize="1.5em" />
                        </Badge>}

                        {isCardInTrade &&<Badge
                            background="#5f9e6e"
                            filter={"grayscale(1)"}
                            borderRadius={"50%"}
                            w="32px"
                            h="32px"
                            display="flex"
                            placeContent="center"
                            alignItems="center"
                            opacity="0.75"
                        >
                            <FaHourglassStart color="#0000008c" fontSize="1.5em" />
                        </Badge>}
                    </Flex>}
                </VStack>}
                {shouldPuff &&<div className="nuage" >
                    <div className="particule"></div>
                    <div className="particule"></div>
                    <div className="particule"></div>
                    <div className="particule"></div>
                    <div className="particule"></div>
                </div>}
            </Box>        
        </Skeleton>
        {/*<SwitchCreature isOpen={isOpen} onClose={onClose} originalCreature={{...creature,creatureId:id}} />*/}
        </>
    )}
